import {
    biasedRandomInt,
    createNode,
    DivideBy,
    IsLeaf,
    rangeToIndex,
    RhythmTree,
    RhythmTreeGenerator,
} from "./index";

// Partially determines possible subdivisions.
const MAX_DEPTH = 4;
const MAX_GRANULARITY = 100;

/**
 * Everything {@link BarTreeGenerator} needs to know, and nothing more.
 *
 * The generator holds a live reference to the config object and reads values
 * at generation time, so mutations take effect when the next bar is
 * generated. Callers wanting live control (e.g. Clicker's settings sliders)
 * pass an object they keep mutating; callers wanting fixed values (e.g.
 * Phraser's presets) pass a plain literal.
 */
interface BarTreeGeneratorConfig {
    granularity: number;
    tupletBias: number;
    beatsPerBar: number;
    /**
     * Minimum subdivision depth every top-level weight group must reach.
     * 1 (the default, used by Clicker) forces every group to subdivide at
     * least once, so no event exceeds one beat - no half notes. 0 (used by
     * Phraser) leaves weight-2 groups free to stay whole, allowing half
     * notes.
     */
    minLeafDepth?: number;
}

/**
 * Intention is to create a bar from a single node. Each subdivision depth should
 * be managed separately in terms of probability, so that we can ensure that subdivisions
 * like whole not triplets are less likely than quarter note triplets, for example.
 *
 * Builds a tree that allows for any number of beats per bar as the root's children.
 * Groups odd numbers into weights of 2s and 1s, which can then be subdivided.
 */
class BarTreeGenerator implements RhythmTreeGenerator {
    private readonly config: BarTreeGeneratorConfig;
    private readonly minLeafDepth: number;

    private readonly isLeaf: IsLeaf = (currentDepth, isTuplet) => {
        // An index into each granularity range, e.g. 0 -> 1 - 25, 1 -> 26 - 50, etc.
        // Basically divides the full granularity range (hardcoded to 100 atm), by the MAX_DEPTH, and
        // then uses the result to convert the current granularity setting into a maxDepth.
        // Then roughly converts the granularity slider into a probability, which translates
        // into how much a beat might be subdivided
        const settingsMaxDepth = rangeToIndex(1, MAX_GRANULARITY, MAX_DEPTH, this.config.granularity);

        const randomisedMaxDepth = biasedRandomInt(0, settingsMaxDepth, 50) + this.minLeafDepth;

        // Effectively, might return a leaf even when not at max depth.
        // And don't allow nested tuplets.
        return currentDepth >= randomisedMaxDepth || isTuplet;
    };

    private readonly subdivideBy: DivideBy = (currentDepth) => {
        // A 100% tuplet bias doesn't work because it means the top level always gets
        // subdivided and then nothing below it (we are not allowing nested tuplets in this algo).
        // Instead, use a combination of the tuplet bias, current depth and granularity to weight more
        // towards tuplets in smaller subdivisions, i.e. lower in the tree. If we are at max
        // tuplet bias, only the deepest level should all be tupletized.
        const settingsMaxDepth = rangeToIndex(1, MAX_GRANULARITY, MAX_DEPTH, this.config.granularity) + this.minLeafDepth;

        // Set the bias based on where the currentDepth is in relation
        // to the settingsMaxDepth. Shallower -> less bias, Deeper -> more bias
        const bias = (this.config.tupletBias / settingsMaxDepth) * (currentDepth + 1);

        return biasedRandomInt(2, 3, bias);
    };

    constructor(config: BarTreeGeneratorConfig) {
        this.config = config;
        this.minLeafDepth = config.minLeafDepth ?? 1;
    }

    // create the top level bar structure, before delegating to the more generic createNode function
    generate(): RhythmTree {
        // Group beatsPerBar into weights of 2s and 1s. This allows for
        // subdivisions of 2 beats at a time, which then allows for half note triplets.
        // each group represents either 1 or 2 'periods' (beats)
        const groups = this.createWeightGroups(this.config.beatsPerBar);

        const children: RhythmTree[] = groups.map((weightGroup) => {
            // Effectively balances the depth of the tree by allowing nodes with more weight (2s)
            // to be subdivided when 1s won't get subdivided. A bit hacky, but it works.
            // An unwanted side effect is that the tree may not go deep enough for low beatsPerMinute settings.
            const newDepth = weightGroup === 1 ? this.minLeafDepth : Math.max(0, this.minLeafDepth - 1);
            return createNode(newDepth, weightGroup, this.isLeaf, this.subdivideBy);
        });

        return {kind: 'node', weight: 1, children};
    }

    /**
     * Takes total weight and returns an array which groups total weight
     * into 2s and 1s, and effectively shuffles the result. For small values (1, 2, 3),
     * returns groups with only 1s.
     * @param totalWeight
     * @private
     */
    private createWeightGroups(totalWeight: number) {
        if (totalWeight <= 0) return [];

        switch (totalWeight) {
            case 1:
                return [1];
            case 2:
                return [1, 1];
            case 3:
                return [1, 1, 1];
            default: {
                const twos = Math.floor(totalWeight / 2);
                const hasRemainder = totalWeight % 2 !== 0;

                const groups: number[] = Array(twos).fill(2);

                if (hasRemainder) {
                    const randomIndex = biasedRandomInt(0, groups.length)
                    groups.splice(randomIndex, 0, 1)
                }

                return groups;
            }
        }
    }
}

export {BarTreeGenerator, BarTreeGeneratorConfig};
